<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { computed, reactive, ref, watch } from 'vue'
	import { Collapse } from 'vue-collapsed'
	import { useRoute } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import StyledCheckbox from '@/components/StyledCheckbox.vue'
	import PayProcessorIcon from '@/components/icons/PayProcessorIcon.vue'

	// import useHotelRate from "../composables/useHotelRate";
	import useAuthUser from '@/composables/useAuthUser'

	import useUserGuaranteeCards from '@/modules/users/composables/useUserGuaranteeCards'
	import useUserOpenPayCard from '@/modules/users/composables/useUserOpenPayCard'

	import { useErrorCodeOpenPay } from '../composables/useErrorCodeOpenPay'
	import useOpenpayValidation from '../composables/useOpenpayValidation'
	import InputEntry from './InputEntry.vue'
	import OpenpaySecurityMessage from './OpenpaySecurityMessage.vue'

	const props = defineProps<{
		isInternational?: boolean
		hotelCountryCode?: string
		paymentMethod?: string
		memberId?: number
		travelerId?: number
		addedTarget?: 'userCards' | 'travelerCards'
	}>()

	const openPay = window.OpenPay
	const toast = useToast()
	const { getErrorCode } = useErrorCodeOpenPay()

	const formModels = reactive({
		holder_name: '',
		card_number: '',
		expiration_year: '',
		expiration_month: '',
		cvv2: '',
	})

	const saving = ref(false)
	const { query } = useRoute()

	const { user } = useAuthUser()

	const targetUserId = computed(() =>
		props.travelerId ?? (user.value!.internal && query.memberId ? Number(query.memberId) : user.value!.id)
	)

	const { createCardAsync, refetch: openPayCardsRefetch } = useUserOpenPayCard({
		params: { user_id: user.value!.id },
		immediate: false,
	})

	const { createCard, refetch } = useUserGuaranteeCards({
		params: { userId: targetUserId.value },
		immediate: false,
	})

	const emit = defineEmits(['added'])
	const { validators, validatePayment } = useOpenpayValidation(
		formModels,
		openPay
	)
	const queryClient = useQueryClient()
	const attemptedToSubmit = ref()
	const guaranteeAccepted = ref(false)

	const isInternationalHotel = computed(() => {
		return props.isInternational || props.hotelCountryCode !== 'MX'
	})

	openPay.setId(import.meta.env.VITE_OPENPAY_ID)
	openPay.setApiKey(import.meta.env.VITE_OPENPAY_KEY)
	openPay.setSandboxMode(
		import.meta.env.VITE_OPENPAY_PRODUCTION_MODE !== 'true'
	)
	const deviceDataId = openPay.deviceData.setup('formId')

	const openPayCreateCard = async () => {
		if (!paymentPayload.cardId || !paymentPayload.deviceId) return

		const targetUserId = props.travelerId ?? user.value!.id

		await createCardAsync({
			params: {
				cardId: paymentPayload.cardId,
				deviceId: paymentPayload.deviceId,
				user_id: targetUserId,
			},
			config: {
				raiseToast: true,
				onSuccessToast: 'Tarjeta guardada correctamente',
				onErrorToast: 'Hubo un error al guardar la tarjeta, intente de nuevo',
			},
		})

		await queryClient.refetchQueries({ queryKey: ['userCards', targetUserId] })

		formModels.card_number = ''
		formModels.holder_name = ''
		formModels.expiration_month = ''
		formModels.expiration_year = ''
		formModels.cvv2 = ''
		attemptedToSubmit.value = false

		emit('added', props.addedTarget ?? 'userCards')
	}

	const createGuaranteeCard = async () => {
		if (!paymentPayload.cardId || !paymentPayload.deviceId || !user.value)
			return

		const data = await createCard({
			userId: targetUserId.value,
			tokenId: paymentPayload.cardId,
			deviceSessionId: paymentPayload.deviceId,
			holderName: formModels.holder_name,
			cvv: formModels.cvv2,
			brand: openPay.card
				.cardType(formModels.card_number.replace(/\s/g, ''))
				.toLowerCase(),
			expiration_year: formModels.expiration_year,
			expiration_month: formModels.expiration_month,
			card_number: formModels.card_number.replace(/\s/g, ''),
			alias: `Tarjeta ${formModels.card_number.slice(-4)}`,
		})

		formModels.card_number = ''
		formModels.holder_name = ''
		formModels.expiration_month = ''
		formModels.expiration_year = ''
		formModels.cvv2 = ''
		attemptedToSubmit.value = false

		await refetch()

		toast.success('Tarjeta guardada correctamente')

		emit('added', 'destination')
	}

	let paymentPayload: {
		cardId: string | undefined
		deviceId: string | undefined
	} = {
		cardId: undefined,
		deviceId: undefined,
	}

	const onSuccess = async (response: any) => {
		saving.value = true
		paymentPayload = {
			cardId: response.data.id,
			deviceId: deviceDataId,
		}

		if (
			paymentPayload.cardId != undefined &&
			paymentPayload.deviceId != undefined
		) {
			try {
				if (isInternationalHotel.value) {
					await createGuaranteeCard()
				} else {
					await openPayCreateCard()
				}
			} catch (error: any) {
				saving.value = false
				// console.log(error);

				if (error.response?.data?.error) {
					toast.error(getErrorCode(error.response?.data?.error?.code))
					return
				}
				toast.error(error.response?.data?.message)
			}
		}
		saving.value = false
		return paymentPayload
	}

	const onError = (response: any) => {
		saving.value = false

		if (response.data.error_code) {
			toast.error(getErrorCode(response?.data?.error_code))
			return
		}
		toast.error(response?.data?.description)
	}

	const handlePay = () => {
		if (saving.value) return
		attemptedToSubmit.value = true

		if (validatePayment()) {
			openPay.token.create(
				{
					...formModels,
					card_number: formModels.card_number.replace(/\s/g, ''),
				},
				onSuccess,
				onError
			)
		}
	}

	const actualProcessor = computed(() =>
		openPay.card
			.cardType(formModels.card_number.replace(/\s/g, ''))
			.toLowerCase()
	)

	const availableProcessors = [
		'american_express',
		'mastercard',
		'visa',
		'carnet',
	]

	const validateProcessor = (processorName: string) => {
		return availableProcessors.includes(processorName)
	}

	watch(formModels, (value) => {
		if (value.card_number) {
			const number = value.card_number.replace(/\s/g, '')
			let formattedNumber = ''

			for (let letter = 0; letter < number.length; letter++) {
				if (letter > 0 && letter % 4 === 0) {
					formattedNumber += ' '
				}

				formattedNumber += number[letter]
			}

			formModels.card_number = formattedNumber
		}
	})
</script>

<template>
	<!-- <div v-if="isLoading">Cargando....</div>
  <div v-else>aaa</div> -->
	<!-- data-screenshot-exclude: dato de tarjeta fuera de la captura del widget de bugs (ver useScreenCapture.ts). -->
	<form class="flex flex-col gap-3 p-1" data-screenshot-exclude>
		<fieldset class="flex w-full flex-col justify-between gap-4 md:flex-row">
			<InputEntry
				v-model="formModels.holder_name"
				label="Titular"
				:error="validators.holder_name.error"
				:attempted-to-submit="attemptedToSubmit"
				placeholder="Nombre y apellido"
			/>

			<InputEntry
				v-model="formModels.card_number"
				label="Número de tarjeta"
				:error="validators.card.error"
				:attempted-to-submit="attemptedToSubmit"
				placeholder="xxxx xxxx xxxx xxxx"
				pattern="/d"
				:max-length="19"
				class="relative"
			>
				<div
					v-if="!actualProcessor"
					v-motion
					:initial="{ opacity: 0 }"
					:enter="{ opacity: 1 }"
					class="hidden min-[450px]:flex items-center gap-2 absolute right-4 top-9"
				>
					<PayProcessorIcon
						v-for="processor in availableProcessors"
						:key="processor"
						:processor="processor"
						class="w-[1.6rem]"
					/>
				</div>

				<div
					v-else-if="validateProcessor(actualProcessor)"
					v-motion
					:initial="{ opacity: 0 }"
					:enter="{ opacity: 1 }"
					class="absolute max-[450px]:right-2 right-4 top-10"
				>
					<PayProcessorIcon
						:processor="actualProcessor"
						class="w-[1.6rem]"
					/>
				</div>
			</InputEntry>
		</fieldset>

		<fieldset class="flex w-full flex-col gap-x-4 gap-y-2 md:flex-row">
			<div class="flex flex-1 flex-col gap-[0.2rem]">
				<div class="flex gap-4">
					<InputEntry
						v-model="formModels.expiration_month"
						label="Mes de expiración"
						placeholder="MM"
						:max-length="2"
					/>

					<InputEntry
						v-model="formModels.expiration_year"
						label="Año de expiración"
						placeholder="AA"
						:max-length="2"
					/>
				</div>

				<Collapse
					:when="!!attemptedToSubmit && !!validators.expiry.error"
					class="v-collapse"
				>
					<span
						v-show="typeof validators.expiry.error == 'string'"
						class="error"
						>{{ validators.expiry.error }}</span
					>
				</Collapse>
			</div>

			<InputEntry
				v-model="formModels.cvv2"
				label="CVC"
				:error="validators.cvv.error"
				:attempted-to-submit="attemptedToSubmit"
				placeholder="CVC"
				:max-length="4"
				class="relative"
			>
				<img
					src="@/assets/imgs/openpay/cvv.png"
					class="max-[450px]:w-[2.5rem] w-[3rem] absolute right-4 max-[450px]:top-9 top-8 sm:top-9"
					alt=""
				/>
			</InputEntry>
		</fieldset>
	</form>

	<OpenpaySecurityMessage
		v-if="!isInternationalHotel"
		class="mt-2 p-1"
	/>

	<div
		v-else
		class="flex justify-between mb-3 mt-3 w-full items-start gap-4 border bg-white p-4 rounded-md cursor-pointer"
	>
		<div class="w-10 h-10">
			<input
				type="checkbox"
				id="guarantee-acceptance"
				v-model="guaranteeAccepted"
				class="mr-2 mt-1 cursor-pointer"
			/>
		</div>

		<label
			for="guarantee-acceptance"
			class="flex-1 cursor-pointer"
		>
			<span class="text-sm text-left">
				He leído y acepto que esta reservación se garantizará con la tarjeta
				proporcionada y que el pago se realizará directamente en el hotel al
				momento de su llegada (check-in). Por favor, asegúrese de contar con una
				forma de pago válida al registrarse.
			</span>
		</label>
	</div>

	<button
		class="ml-auto w-fit rounded-md border p-2 px-4 bg-[#39d6cb] text-[#004485] font-bold hover:bg-[#00b3a7] hover:text-white transition-colors duration-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#39d6cb] disabled:hover:text-[#004485]"
		:disabled="saving || (isInternationalHotel && !guaranteeAccepted)"
		@click.prevent="handlePay"
	>
		<template v-if="saving">
			<svg
				class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
			>
				<circle
					class="opacity-25"
					cx="12"
					cy="12"
					r="10"
					stroke="currentColor"
					stroke-width="4"
				></circle>
				<path
					class="opacity-75"
					fill="currentColor"
					d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
				></path>
			</svg>
		</template>
		<template v-else> Guardar tarjeta </template>
	</button>
</template>

<style scoped lang="css">
	input {
		width: 100%;
		border-radius: 5px;
	}

	label {
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
	}

	.error {
		@apply p-1 text-red-500;
	}
</style>
